import { useCallback, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { supabase, isDevBypass, DEV_BYPASS_USER_ID } from '../lib/auth';
import { track } from '../lib/analytics';

async function apiDelete(path: string): Promise<void> {
  await apiFetch(path, { method: 'DELETE' });
}

export interface TeamSettingsMember {
  user_id: string;
  display_name: string;
  display_role: string;
  role: string;
  joined_at: string;
  visibility_tier: string;
}

export interface TeamSettingsInvite {
  id: string;
  email: string;
  invited_by: string;
  created_at: string;
  expires_at: string;
}

export interface TeamSettingsData {
  id: string;
  name: string;
  created_at: string;
  members: TeamSettingsMember[];
  pending_invites: TeamSettingsInvite[];
}

interface TeamSettingsResponse {
  teams: TeamSettingsData[];
}

export function useTeamSettings() {
  const query = useQuery({
    queryKey: ['surfaces', 'team-settings'],
    queryFn: async () => {
      const res = await apiFetch<TeamSettingsResponse>('/api/surfaces/team/settings');
      return res.teams[0] ?? null;
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  return { ...query, data: query.data ?? undefined };
}

export function useCurrentUserId() {
  return useQuery({
    queryKey: ['auth', 'user-id'],
    queryFn: async () => {
      // Dev/test bypass has no real session, so getUser() returns null — fall back to a
      // stable fake id so "You"-keyed surfaces (People) render against MSW fixtures.
      if (isDevBypass()) return DEV_BYPASS_USER_ID;
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id ?? null;
    },
    staleTime: Infinity,
  });
}

export function useRenameTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, name }: { teamId: string; name: string }) => {
      await apiFetch(`/api/teams/${teamId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surfaces', 'team-settings'] }),
  });
}

export function useDeleteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (teamId: string) => {
      await apiDelete(`/api/teams/${teamId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surfaces', 'team-settings'] });
      qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useSendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, email }: { teamId: string; email: string }) => {
      await apiFetch(`/api/teams/${teamId}/invite`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surfaces', 'team-settings'] }),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, inviteId }: { teamId: string; inviteId: string }) => {
      await apiDelete(`/api/teams/${teamId}/invite/${inviteId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surfaces', 'team-settings'] }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, userId, updates }: {
      teamId: string;
      userId: string;
      updates: { display_name?: string; display_role?: string; visibility_tier?: string; role?: string };
    }) => {
      await apiFetch(`/api/teams/${teamId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surfaces', 'team-settings'] });
      qc.invalidateQueries({ queryKey: ['surfaces', 'team-members'] });
    },
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, userId }: { teamId: string; userId: string }) => {
      await apiDelete(`/api/teams/${teamId}/members/${userId}`);
    },
    onSuccess: (_data, { teamId }) => {
      track('member_removed', { team_id: teamId });
      qc.invalidateQueries({ queryKey: ['surfaces', 'team-settings'] });
    },
  });
}

export interface PendingInvite {
  id: string;
  team_id: string;
  team_name: string;
  invited_by_email: string;
  created_at: string;
  expires_at: string;
}

export interface PendingInvitesData {
  invites: PendingInvite[];
  current_team: { id: string; name: string; member_count: number } | null;
}

export function usePendingInvites() {
  return useQuery({
    queryKey: ['teams', 'invites', 'pending'],
    queryFn: () => apiFetch<PendingInvitesData>('/api/teams/invites/pending'),
    staleTime: 60 * 1000,
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, inviteId }: { teamId: string; inviteId: string }) => {
      await apiFetch(`/api/teams/${teamId}/invite/${inviteId}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'accept' }),
      });
    },
    // Accepting an invite moves the user to a DIFFERENT team, so every team-scoped query is now
    // wrong — the briefing, the map, conversations, standup, sources. The four targeted keys this
    // replaced covered a fraction of that, which is why both call sites papered over it with a full
    // document reload (window.location.reload / location.assign). Invalidate everything instead:
    // active queries refetch, the SPA is never torn down, and no caller needs a reload to be right.
    onSuccess: () => {
      qc.invalidateQueries();
    },
  });
}

export function useDeclineInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, inviteId }: { teamId: string; inviteId: string }) => {
      await apiFetch(`/api/teams/${teamId}/invite/${inviteId}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'decline' }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams', 'invites', 'pending'] }),
  });
}

// "Make sure the user has a team, returning its id." bug-450: this delegates to the ONE
// idempotent, race-safe backend creator — POST /api/surfaces/team/ensure → the advisory-locked
// ensure_team_for_user RPC (get-or-create the user's oldest team). The OLD client-side path
// decided "have I a team?" from a react-query cache allowed to be 60s stale and then created a
// team ITSELF, so a first-run user racing a team-surface load ended up with TWO teams and an
// invite split one-per-team (it "sent" but showed on neither). An in-flight ref also collapses
// concurrent ensure() calls (a double-clicked "Send invite") into a single request. Used by
// first-run onboarding to invite teammates or connect a team source (Slack/Notion) before the
// user has explicitly created a team — bug-447 / bug-448.
export function useEnsureTeam() {
  const qc = useQueryClient();
  const inFlight = useRef<Promise<string> | null>(null);
  const [isPending, setIsPending] = useState(false);

  const ensure = useCallback((): Promise<string> => {
    if (inFlight.current) return inFlight.current;
    setIsPending(true);
    const p = apiFetch<{ team_id: string }>('/api/surfaces/team/ensure', { method: 'POST' })
      .then((res) => res.team_id);
    inFlight.current = p;
    void p
      .catch(() => {})
      .finally(() => {
        inFlight.current = null;
        setIsPending(false);
        // A team may have just been created — refresh anything keyed on membership.
        qc.invalidateQueries({ queryKey: ['teams', 'invites', 'pending'] });
        qc.invalidateQueries({ queryKey: ['surfaces', 'team-settings'] });
      });
    return p;
  }, [qc]);

  return { ensure, isPending };
}
